import {
  useEffect,
  useState,
  forwardRef,
  useRef,
  useMemo,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { clusterApi, TenantUsageStats } from "../services/api";
import ClientManagementModal from "./ClientManagementModal";
import ChangePasswordModal from "./ChangePasswordModal";
import { useToast } from "./Toast";
import HomeIcon from "@mui/icons-material/Home";
import "./Header.css";
import RefreshMetricsBtn from "./RefreshMetricsBtn";

export interface HeaderRef {
  refreshTrackedKeywordsCount: () => Promise<void>;
}

interface HeaderProps {
  setMaxKeywordsPerClusterLimit?: (value: number | "inf") => void;
  onClickAddAnchorKeyword?: () => void;
  hasValidDomain?: boolean;
  currentPage?: "admin-panel" | "dashboard";
  onDomainUpdated?: () => void;
}

const Header = forwardRef<HeaderRef, HeaderProps>(({ setMaxKeywordsPerClusterLimit, onClickAddAnchorKeyword, hasValidDomain, currentPage = 'dashboard', onDomainUpdated }, ref) => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [isLoadingTenant, setIsLoadingTenant] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [usageStats, setUsageStats] = useState<TenantUsageStats | null>(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const { showToast } = useToast();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchCurrentTenant = async () => {
      if (!isAuthenticated) {
        setTenantName("");
        return;
      }
      try {
        setIsLoadingTenant(true);
        const tenant = await clusterApi.getCurrentTenant();
        setTenantName(tenant.name || "");
      } catch (error) {
        console.error("Failed to fetch current tenant:", error);
        setTenantName("");
      } finally {
        setIsLoadingTenant(false);
      }
    };

    fetchCurrentTenant();
  }, [isAuthenticated]);
  
  // Fetch usage stats
  useEffect(() => {
    const fetchUsageStats = async () => {
      if (!isAuthenticated) {
        setUsageStats(null);
        return;
      }
      try {
        const stats = await clusterApi.getMyUsage();
        setUsageStats(stats);
        setMaxKeywordsPerClusterLimit?.(stats.max_keywords_per_cluster ?? "inf");
      } catch (error) {
        console.error("Failed to fetch usage stats:", error);
        setUsageStats(null);
      }
    };

    fetchUsageStats();
  }, [isAuthenticated]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    setShowUserDropdown(false);
    logout();
    navigate("/login");
  };

  // Get user initials for avatar
  const userInitials = useMemo(() => {
    if (!user?.email) return "?";
    const email = user.email;
    const parts = email.split("@")[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.slice(0, 2).toUpperCase();
  }, [user?.email]);

  return (
    <header className="app-header">
      <div className="header-content">
        <div className="header-title">
          <a href="/">
            <img
              src="/clusterview-logo-with-text.svg"
            />
          </a>
        </div>

        <div className="header-auth">
          {isAuthenticated ? (
            <div className="user-info">
              {currentPage === 'dashboard' && (
                <>
                  <button
                    className="flex items-center gap-2 bg-white p-2 min-w-48 rounded-full font-medium h-12"
                    onClick={() => setIsClientModalOpen(true)}
                  >
                    <img src="/icons/domain.svg" />
                    <span>Domains</span>
                  </button>
    
                  {onClickAddAnchorKeyword && isAuthenticated && (
                    <button 
                      className="flex items-center gap-1.5 bg-my-orange rounded-full px-4 h-12"
                      onClick={() => {
                        if (user && !user.email_verified) {
                          showToast("error", "Please verify your email address first before using this app!")
                          return;
                        }
                        if (!hasValidDomain) {
                          showToast("error", "Please create a domain first before adding clusters. Click the 'Domains' button!")
                          return;
                        }
                        onClickAddAnchorKeyword();
                      }}
                    >
                      <img src="/icons/plus.svg" />
                      <span className="text-white font-semibold">Add Anchor Keyword</span>
                    </button>
                  )}
                </>
              )}

              {currentPage === 'admin-panel' && user?.is_superadmin && (
                <RefreshMetricsBtn />
              )}

              {/* Avatar and its Dropdown */}
              <div 
                className="rounded-full aspect-square h-12 relative"
                onClick={() => setShowUserDropdown(true)}
                ref={dropdownRef}
              >
                <button className="user-avatar">{userInitials}</button>

                {showUserDropdown && (
                  <div className="bg-white absolute right-0 top-[calc(100%+6px)] rounded-[30px] p-5 z-30 shadow-sm w-[28rem]">
                    <div className="w-full flex flex-col gap-3">
                      <div className="flex items-center gap-4">
                        <div className="w-[60px] h-[60px]">
                          <div className="user-avatar">{userInitials}</div>
                        </div>

                        <div className="flex flex-col gap-y-1">
                          <p className="font-semibold text-lg leading-[1]">{user?.email}</p>
                          <p className="font-medium text-sm text-stone-400 leading-[1]">{user?.is_superadmin ? "Superadmin" : user?.role}</p>
                          <p className="font-medium text-sm text-stone-400 leading-[1]">
                            {isLoadingTenant ? (
                              <div className="h-3.5 w-28 bg-neutral-200 animate-pulse rounded-md"></div>
                            ) : (
                              <>{tenantName}</>
                            )}
                          </p>
                        </div>
                      </div>

                      {user && (user.role === "admin" || user.is_superadmin) && (
                        <a
                          href={currentPage === 'dashboard' ? "/admin" : "/"}
                          className="flex justify-between items-center bg-my-light-purple p-4 rounded-full active:scale-95 transition-all"
                        >
                          <div className="flex items-center gap-2">
                            {currentPage === 'dashboard' ? (
                              <img src='/icons/user-round.svg' />
                            ) : (
                              <HomeIcon style={{ color: "#8642C7" }} />
                            )}
                            <span className="font-semibold text-my-purple">
                              {currentPage === 'dashboard' ? 'Account Management' : 'Dashboard'}
                            </span>
                          </div>

                          <img src='/icons/chevron-right.svg' />
                        </a>
                      )}
                    </div>

                    <div className="my-[30px]">
                      <h2 
                        style={{ fontFamily: "Roundo" }}
                        className="font-bold text-[26px] mb-1"
                      >
                        Plan Usage {usageStats?.is_unlimited ? '∞' : ''}
                      </h2>

                      {usageStats ? (
                        <div className="plan-usage-boxes">
                          <div className="bg-[#fce1d8]">
                            <div className="title">Domains</div>
                            <div className="data">
                              {usageStats.domains_used}
                              /
                              {usageStats.domains_limit ?? '∞'}
                            </div>
                          </div>
                          <div className="bg-[#F3F4FF]">
                            <div className="title">Clusters</div>
                            <div className="data">
                              {usageStats.clusters_used}
                              /
                              {usageStats.clusters_limit ?? '∞'}
                            </div>
                          </div>
                          <div className="bg-[#F3F4FF]">
                            <div className="title">Users</div>
                            <div className="data">
                              {usageStats.users_used}
                              /
                              {usageStats.users_limit ?? '∞'}
                            </div>
                          </div>
                          <div className="bg-[#F3F4FF]">
                            <div className="title">Max Keywords/Cluster</div>
                            <div className="data">
                              {usageStats.max_keywords_per_cluster ?? '∞'}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="plan-usage-boxes">
                          <div className="bg-neutral-200 animate-pulse h-[87px]" />
                          <div className="bg-neutral-200 animate-pulse h-[87px]" />
                          <div className="bg-neutral-200 animate-pulse h-[87px]" />
                          <div className="bg-neutral-200 animate-pulse h-[87px]" />
                        </div>
                      )}
                    </div>

                    <div className="border-t-[1.5px] border-t-neutral-200 pt-5">
                      <button 
                        className="flex items-center gap-2 px-4 py-2.5 hover:bg-red-50 rounded-full w-full"
                        onClick={handleLogout}
                      >
                        <img src='/icons/log-out.svg' />
                        <span className="text-[#EE693F] font-semibold">Log Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <Link to="/login" className="login-btn">
              Login
            </Link>
          )}
        </div>
      </div>

      {/* Client Management Modal */}
      <ClientManagementModal
        isOpen={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
        onDomainUpdated={onDomainUpdated}
      />

      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => setShowChangePassword(false)}
          onSuccess={() => {
            setShowChangePassword(false);
            showToast("success", "Password changed successfully");
          }}
        />
      )}
    </header>
  );
});

Header.displayName = "Header";

export default Header;
